import { useEffect, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePublicSettings } from "@/hooks/usePublicSettings";

/**
 * Paths that stay reachable while maintenance mode is on, so an administrator
 * can always sign in and reach the console to switch it back off.
 */
const ALLOWED_PREFIXES = ["/admin", "/login"];

const FAVICON_ID = "cbc-dynamic-favicon";
const FONT_STYLE_ID = "cbc-dynamic-font";

function applyFavicon(url: string): void {
  const existing = document.getElementById(FAVICON_ID) as HTMLLinkElement | null;
  if (!url) {
    if (existing) existing.remove();
    return;
  }
  const link = existing ?? document.createElement("link");
  if (!existing) {
    link.id = FAVICON_ID;
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.type = url.endsWith(".svg") ? "image/svg+xml" : "image/png";
  link.href = url;
}

function applyFont(url: string, family: string): void {
  let style = document.getElementById(FONT_STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.id = FONT_STYLE_ID;
    document.head.appendChild(style);
  }
  if (!url) {
    style.textContent = "";
    return;
  }
  const fam = (family || "CustomBrand").replace(/["\\]/g, "");
  style.textContent = [
    `@font-face{font-family:"${fam}";src:url("${url}");font-display:swap;}`,
    `:root{--font-heading:"${fam}",ui-sans-serif,system-ui,sans-serif;--font-body:"${fam}",ui-sans-serif,system-ui,sans-serif;}`,
  ].join("\n");
}

/**
 * App shell guard.
 *
 * Reads the public site settings once and:
 *   1. Applies the branding assets (favicon + custom web font) to the document.
 *   2. Blocks the public site with a maintenance screen when Maintenance Mode is
 *      on — while keeping /admin and /login reachable and letting signed-in
 *      administrators browse normally.
 */
export default function MaintenanceGate({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { user, isLoading } = useAuth();
  const { settings, loaded } = usePublicSettings();

  useEffect(() => {
    applyFavicon(settings.faviconUrl);
    applyFont(settings.fontUrl, settings.fontFamily);
  }, [settings.faviconUrl, settings.fontUrl, settings.fontFamily]);

  // Don't block anything until the settings have resolved (avoids a flash).
  if (!loaded || isLoading) return <>{children}</>;

  const isAdmin = user?.role === "admin";
  const allowedPath = ALLOWED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

  if (!settings.maintenanceMode || isAdmin || allowedPath) {
    return <>{children}</>;
  }

  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center bg-background-50 px-6 py-16 text-center">
      <div className="w-full max-w-lg">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-100 text-primary-700">
          <i className="ri-tools-line text-3xl" />
        </span>
        <h1 className="mt-6 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          {settings.siteName || "We'll be right back"}
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
          We&apos;re currently making a few improvements and will be back online shortly. Thanks for
          your patience.
        </p>
        {settings.tagline && (
          <p className="mt-2 text-sm font-medium text-foreground-500">{settings.tagline}</p>
        )}

        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-refresh-line text-base" />
            </span>
            Try again
          </button>
          <Link
            to="/admin"
            className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-shield-user-line text-base" />
            </span>
            Admin sign in
          </Link>
        </div>

        {settings.supportEmail && (
          <p className="mt-6 text-xs text-foreground-500">
            Need help now?{" "}
            <a href={`mailto:${settings.supportEmail}`} className="font-semibold text-primary-700">
              {settings.supportEmail}
            </a>
          </p>
        )}
      </div>
    </div>
  );
}